import { ChangeType } from '@common/prosemirror/tracked-changes/change-type.enum';
import { TrackedChangeNodeAttrs } from '@common/prosemirror/tracked-changes/tracked-change-node-attrs.type';
import { TrackedChange } from '@common/prosemirror/tracked-changes/tracked-change.type';
import { isEqual } from 'lodash';
import { Mark, MarkType } from 'prosemirror-model';

/**
 * Returns true if any of the change attrs pass a test by the given iteratee.
 * @param attrs The change node attrs to check.
 * @param iteratee A callback function that takes a TrackedChange as an argument and returns true or false.
 * @returns True if the callback function returns true for any of the changes in the attrs.
 */
export function attrsHaveChangeBy(attrs: TrackedChangeNodeAttrs, iteratee: (change: TrackedChange) => boolean): boolean {
  if (attrs && Array.isArray(attrs.changeList)) {
    return attrs.changeList.some(iteratee);
  }

  return false;
}

/**
 * attrsHaveChange
 * Returns true if the attrs include the given change type.
 * @param attrs The attributes to check for the change.
 * @param changeType The change type to check for.
 */
export function attrsHaveChange(attrs: TrackedChangeNodeAttrs, changeType: ChangeType): boolean {
  return attrsHaveChangeBy(attrs, change => changeType === change.changeType);
}

/*
 * marksHaveChange
 * Returns true if the mark set contains a change mark. Optionally pass a changeType to check if the mark's change is of the same type
 */
export function marksHaveChange(changeMarkType: MarkType, marks: readonly Mark[], changeType?: ChangeType): boolean {
  return Array.isArray(marks) && marks.some(mark => {
    if (mark.type === changeMarkType) {
      if (changeType) {
        return attrsHaveChange(mark.attrs as TrackedChangeNodeAttrs, changeType);
      }

      return true;
    }

    return false;
  });
}

/**
 * Returns true if the changes are the same for two change node attrs.
 * @param attrsA The attributes to compare.
 * @param attrsB The other attributes to compare.
 * @return Returns true if the changes are the same for two change node attrs.
 */
export function changeNodeAttrsAreEqual(attrsA: TrackedChangeNodeAttrs, attrsB: TrackedChangeNodeAttrs): boolean {
  // Empty/falsy attributes and change lists are considered equal so reduce them down to be arrays before comparing them
  const changeListA = attrsA && Array.isArray(attrsA.changeList) ? attrsA.changeList : [];
  const changeListB = attrsB && Array.isArray(attrsB.changeList) ? attrsB.changeList : [];

  return isEqual(changeListA, changeListB);
}
